import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { AlertTriangle, XCircle, CheckCircle, Info } from 'lucide-react';

export interface ValidationViolation {
  type: string;
  message: string;
  current: number;
  limit: number;
  overage: number;
}

export interface PlanChangeValidation {
  allowed: boolean;
  is_downgrade: boolean;
  is_upgrade: boolean;
  violations: ValidationViolation[];
  current_usage: {
    credits: number;
    syncs: number;
    stores: number;
  };
  target_limits: {
    credits: number;
    credits_effective?: number;
    syncs: number;
    stores: number;
  };
  current_plan: {
    id: string;
    name: string;
    limits: {
      credits: number;
      credits_effective?: number;
      syncs: number;
      stores: number;
    };
  };
  promo?: {
    multiplier: number;
    description: string;
  } | null;
}

interface PlanChangeValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  validation: PlanChangeValidation | null;
  targetPlanName?: string;
  onProceed?: () => void;
  isProcessing?: boolean;
}

export const PlanChangeValidationModal: React.FC<PlanChangeValidationModalProps> = ({
  isOpen,
  onClose,
  validation,
  targetPlanName,
  onProceed,
  isProcessing = false,
}) => {
  if (!validation) return null;

  const getViolationIcon = (type: string) => {
    switch (type) {
      case 'credits':
        return '💳';
      case 'syncs':
        return '🔄';
      case 'stores':
        return '🔌';
      default:
        return '⚠️';
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 999999) return '∞';
    return num.toLocaleString();
  };

  const canProceed = validation.allowed || !validation.is_downgrade;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {validation.allowed ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                Plan Change Available
              </>
            ) : (
              <>
                <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                Plan Change Restricted
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {validation.is_downgrade ? 'Downgrading' : 'Upgrading'} from {validation.current_plan.name} to {targetPlanName || 'selected plan'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Show promo info if active */}
          {validation.promo && (
            <Alert className="border-orange-200 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-800">
              <Info className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              <AlertDescription className="text-orange-800 dark:text-orange-200">
                <strong>2X Credits Active!</strong> Your {validation.promo.description} bonus will apply to the new plan too.
              </AlertDescription>
            </Alert>
          )}

          {/* Show violations if any */}
          {validation.violations.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground">Usage Violations</h4>
              {validation.violations.map((violation, index) => (
                <Alert key={index} variant="destructive">
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                  <AlertDescription className="space-y-2">
                    <div className="font-semibold flex items-center gap-2">
                      <span>{getViolationIcon(violation.type)}</span>
                      {violation.type.charAt(0).toUpperCase() + violation.type.slice(1)} Limit Exceeded
                    </div>
                    <div className="text-sm">{violation.message}</div>
                    <div className="flex gap-4 text-xs mt-2">
                      <div>
                        <span className="text-muted-foreground">Current:</span>{' '}
                        <span className="font-semibold text-red-600">{formatNumber(violation.current)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Limit:</span>{' '}
                        <span className="font-semibold">{formatNumber(violation.limit)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Over by:</span>{' '}
                        <span className="font-semibold text-red-600">{formatNumber(violation.overage)}</span>
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          )}

          {/* Usage comparison table */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-muted-foreground">Usage Comparison</h4>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Resource</th>
                    <th className="text-right p-3 font-medium">Current Usage</th>
                    <th className="text-right p-3 font-medium">Current Limit</th>
                    <th className="text-right p-3 font-medium">New Limit</th>
                    <th className="text-center p-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {/* Credits */}
                  <tr>
                    <td className="p-3">💳 Credits</td>
                    <td className="text-right p-3 font-mono">{formatNumber(validation.current_usage.credits)}</td>
                    <td className="text-right p-3 font-mono">
                      {validation.promo ? (
                        <span className="flex items-center justify-end gap-1">
                          {formatNumber(validation.current_plan.limits.credits_effective || validation.current_plan.limits.credits)}
                          <Badge className="bg-orange-500 text-white text-[10px] px-1 py-0">2X</Badge>
                        </span>
                      ) : (
                        formatNumber(validation.current_plan.limits.credits)
                      )}
                    </td>
                    <td className="text-right p-3 font-mono">
                      {validation.promo ? (
                        <span className="flex items-center justify-end gap-1">
                          {formatNumber(validation.target_limits.credits_effective || validation.target_limits.credits)}
                          <Badge className="bg-orange-500 text-white text-[10px] px-1 py-0">2X</Badge>
                        </span>
                      ) : (
                        formatNumber(validation.target_limits.credits)
                      )}
                    </td>
                    <td className="text-center p-3">
                      {validation.current_usage.credits <= (validation.target_limits.credits_effective || validation.target_limits.credits) ? (
                        <Badge variant="outline" className="bg-green-50 dark:bg-green-950/50 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800">OK</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-red-50 dark:bg-red-950/50 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800">Over</Badge>
                      )}
                    </td>
                  </tr>
                  {/* Syncs */}
                  <tr>
                    <td className="p-3">🔄 Syncs</td>
                    <td className="text-right p-3 font-mono">{formatNumber(validation.current_usage.syncs)}</td>
                    <td className="text-right p-3 font-mono">{formatNumber(validation.current_plan.limits.syncs)}</td>
                    <td className="text-right p-3 font-mono">{formatNumber(validation.target_limits.syncs)}</td>
                    <td className="text-center p-3">
                      {validation.current_usage.syncs <= validation.target_limits.syncs ? (
                        <Badge variant="outline" className="bg-green-50 dark:bg-green-950/50 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800">OK</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-red-50 dark:bg-red-950/50 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800">Over</Badge>
                      )}
                    </td>
                  </tr>
                  {/* Connectors */}
                  <tr>
                    <td className="p-3">🔌 Connectors</td>
                    <td className="text-right p-3 font-mono">{formatNumber(validation.current_usage.stores)}</td>
                    <td className="text-right p-3 font-mono">{formatNumber(validation.current_plan.limits.stores)}</td>
                    <td className="text-right p-3 font-mono">{formatNumber(validation.target_limits.stores)}</td>
                    <td className="text-center p-3">
                      {validation.current_usage.stores <= validation.target_limits.stores ? (
                        <Badge variant="outline" className="bg-green-50 dark:bg-green-950/50 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800">OK</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-red-50 dark:bg-red-950/50 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800">Over</Badge>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Info message */}
          {!validation.allowed && validation.is_downgrade && (
            <Alert>
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription>
                <strong>What can you do?</strong>
                <ul className="mt-2 space-y-1 text-sm list-disc list-inside">
                  <li>Wait until the next billing cycle when your usage resets</li>
                  {validation.violations.some(v => v.type === 'stores') && (
                    <li>Remove some connectors to reduce your connector count</li>
                  )}
                  <li>Contact support if you need assistance</li>
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {validation.allowed && (
            <Alert className="border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800">
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
              <AlertDescription className="text-green-800 dark:text-green-200">
                Your plan change has been validated. You can proceed to update your subscription.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isProcessing}>
            Cancel
          </Button>
          {canProceed && onProceed && (
            <Button onClick={onProceed} disabled={isProcessing}>
              {isProcessing ? 'Processing...' : 'Continue to Checkout'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};